import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Star, Truck, ShieldCheck, Gift, Sparkles, Moon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatCents } from "@/lib/format";
import { track } from "@/lib/tracking";
import { JsonLd } from "@/components/site/JsonLd";
import { pageQueryOptions } from "@/lib/pages.functions";
import { useSitePage } from "@/components/site/EditablePageShell";
import { ExperimentCard } from "@/components/site/ExperimentCard";
import heroImage from "@/assets/hero-night-fairway.jpg";
import sixPackGlow from "@/assets/product-six-pack-glow.jpg";
import sixPackWhite from "@/assets/product-six-pack-white.jpg";
import backyard from "@/assets/lifestyle-backyard.jpg";
import packagingOpen from "@/assets/packaging-open.jpg";
import miniGolf from "@/assets/mini-golf.jpg";
import backyardPractice from "@/assets/lifestyle-backyard-practice.jpg";
import campingResort from "@/assets/lifestyle-camping-resort.jpg";
import corporateEvent from "@/assets/lifestyle-corporate-event.jpg";
import teeGlow from "@/assets/hero-tee-glow.jpg";
import { pickHomeShareImage } from "@/lib/share-images";

export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    const page = await context.queryClient.ensureQueryData(pageQueryOptions("home"));
    // Pick a random share image per request — server runs this for every
    // crawler fetch, so previews vary across shares.
    return { page, shareImage: pickHomeShareImage() };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.page?.title || "Glow-in-the-Dark Golf Balls — Glow Long Golf" },
      { name: "description", content: loaderData?.page?.meta_description || "Six bright glow-in-the-dark golf balls per pack — red, green, pink, white, blue, orange. Charge under any light. Built for night golf." },
      { property: "og:title", content: loaderData?.page?.title || "Glow-in-the-Dark Golf Balls — Glow Long Golf" },
      { property: "og:description", content: loaderData?.page?.meta_description || "Six glow colors per pack. Built for night golf, gifts, and parties." },
      { property: "og:url", content: "https://glowlonggolf.com/" },
      { property: "og:image", content: loaderData?.shareImage },
      { name: "twitter:image", content: loaderData?.shareImage },
    ],
    links: [{ rel: "canonical", href: "https://glowlonggolf.com/" }],
  }),

  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive" role="alert">{error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-sm text-muted-foreground">Not found.</div>,
  component: HomePage,
});

function useFeaturedVariant() {
  return useQuery({
    queryKey: ["featured-variant"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_variants")
        .select("id,name,price_cents,compare_at_cents,product_id,products!inner(slug,title,short_description)")
        .eq("sku", "GLG-6PACK")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function HomePage() {
  const { data: variant } = useFeaturedVariant();
  const page = useSitePage("home");
  const productSlug = (variant?.products as any)?.slug ?? "glow-long-6-pack";
  const price = variant?.price_cents ?? 1799;
  const headings = (page?.extra?.section_headings ?? {}) as { featured?: string; how_it_works?: string; reviews?: string };
  const extraSteps = Array.isArray(page?.extra?.how_it_works_steps) ? page!.extra.how_it_works_steps : null;
  const steps = (extraSteps && extraSteps.length === 3 ? extraSteps : STEPS);
  const heroEyebrowText = page?.hero_eyebrow?.trim() || "New: 6 glowing colors in every pack";
  const heroTitleText = page?.hero_title?.trim() || "";
  const heroLeadText = page?.hero_lead?.trim() || "Six brilliantly bright glow balls — one each of red, green, pink, white, blue, and orange. Charge under any light, then tee off after dark.";

  useEffect(() => { track("page_view", { page: "home" }); }, []);

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Glow Long Golf",
          description: "Glow-in-the-dark golf balls for night golf, gifts, and events.",
        }}
      />

      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 bg-radial-glow opacity-90" aria-hidden />
        <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 pb-16 pt-12 sm:px-6 md:grid-cols-2 md:pb-24 md:pt-20 lg:px-8">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" /> {heroEyebrowText}
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
              {heroTitleText ? heroTitleText : (<>Golf balls that <span className="text-primary text-glow">light up the dark</span>.</>)}
            </h1>
            <p className="mt-5 max-w-lg text-lg text-muted-foreground">
              {heroLeadText}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/products/$slug"
                params={{ slug: productSlug }}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow-strong transition hover:opacity-90"
              >
                Shop Glow Golf Balls <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/amazon"
                onClick={() => track("click_amazon_cta", { location: "home_hero" })}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-medium hover:bg-secondary"
              >
                Buy on Amazon
              </Link>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{formatCents(price)}</span> per 6-pack ·
              <span className="ml-1">Ships from Amazon · Free shipping over $50</span>
            </p>
            <div className="mt-8 flex items-center gap-1 text-sm text-muted-foreground">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-primary text-primary" />
              ))}
              <span className="ml-2">4.9 from happy late-night golfers</span>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden rounded-3xl border border-border bg-card shadow-glow">
              <img
                src={heroImage}
                alt="Six glow-in-the-dark golf balls — red, green, pink, blue, white, orange — lighting up a dark golf fairway at dusk"
                className="h-full w-full object-cover"
                width={1920}
                height={1280}
                fetchPriority="high"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden h-32 w-32 overflow-hidden rounded-2xl border border-border bg-background shadow-glow md:block">
              <img src={teeGlow} alt="Glowing green golf ball on a tee" className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section aria-label="Trust" className="border-y border-border/60 bg-card/30">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-6 text-sm sm:grid-cols-4 sm:px-6 lg:px-8">
          <TrustItem icon={<ShieldCheck className="h-4 w-4 text-primary" />} label="Secure checkout" />
          <TrustItem icon={<Truck className="h-4 w-4 text-primary" />} label="Ships from Amazon" />
          <TrustItem icon={<Gift className="h-4 w-4 text-primary" />} label="Gift-ready packaging" />
          <TrustItem icon={<Moon className="h-4 w-4 text-primary" />} label="Built for night golf" />
        </div>
      </section>

      {/* USE CASES */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl font-semibold sm:text-4xl">Built for every kind of round</h2>
          <p className="mt-3 text-muted-foreground">Six colors, one pack — pick your favorite or split with friends.</p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {USE_CASES.map((u) => (
            <ExperimentCard key={u.experimentKey} experimentKey={u.experimentKey} fallback={{ label: u.label, blurb: u.blurb, image: u.image }} />
          ))}
        </div>
      </section>

      {/* PRODUCT FEATURE */}
      <section className="border-y border-border/60 bg-card/30">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-20 sm:px-6 md:grid-cols-2 lg:px-8">
          <div className="overflow-hidden rounded-3xl border border-border bg-background">
            <img src={sixPackGlow} alt="Six glowing golf balls on a black background" className="h-full w-full object-cover" loading="lazy" />
          </div>
          <div>
            <h2 className="font-display text-3xl font-semibold sm:text-4xl">One pack. Six colors. Endless rounds.</h2>
            <p className="mt-4 text-muted-foreground">
              Every Glow Long 6-pack ships with one of every color so groups can tell their shots apart, gift the rest, or build their favorite color collection.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary" /> {b}
                </li>
              ))}
            </ul>
            <Link
              to="/products/$slug"
              params={{ slug: productSlug }}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90"
            >
              Shop the 6-pack — {formatCents(price)} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl font-semibold sm:text-4xl">{headings.how_it_works || "How it works"}</h2>
          <p className="mt-3 text-muted-foreground">Drop it, tee it up, and bounce it back to life. Zero batteries.</p>
        </div>
        <ol className="mt-10 grid gap-6 md:grid-cols-3">
          {steps.map((s: any, i: number) => (
            <li key={i} className="rounded-2xl border border-border bg-card p-6">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                {i + 1}
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* PACKAGING / REVIEWS */}
      <section className="mx-auto grid max-w-7xl gap-10 px-4 pb-20 sm:px-6 md:grid-cols-2 lg:px-8">
        <div className="overflow-hidden rounded-3xl border border-border bg-background">
          <img src={packagingOpen} alt="Open matte-black mailer box with six glowing golf balls inside a foam insert" className="h-full w-full object-cover" loading="lazy" />
        </div>
        <div className="rounded-3xl border border-border bg-card/40 p-8">
          <h2 className="font-display text-3xl font-semibold">{headings.reviews || "Loved by late-night golfers"}</h2>
          <div className="mt-6 space-y-6">
            {TESTIMONIALS.map((t) => (
              <figure key={t.name}>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <blockquote className="mt-2 text-sm text-foreground">"{t.body}"</blockquote>
                <figcaption className="mt-1 text-xs text-muted-foreground">— {t.name}, {t.loc}</figcaption>
              </figure>
            ))}
          </div>
          <Link to="/reviews" className="mt-6 inline-block text-sm font-semibold text-primary hover:underline">
            See all reviews →
          </Link>
        </div>
      </section>

      {/* LIFESTYLE STRIP */}
      <section className="relative isolate overflow-hidden border-y border-border/60">
        <img src={backyard} alt="Friends playing golf in a backyard at night around a fire pit" className="absolute inset-0 h-full w-full object-cover opacity-40" loading="lazy" />
        <div className="relative mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-semibold sm:text-5xl">
            The night golf round <span className="text-primary text-glow">everyone remembers</span>.
          </h2>
          <p className="mt-4 text-muted-foreground">Backyards, bachelor parties, corporate events, resorts, par-3 twilight rounds. If it happens after sunset, Glow Long belongs there.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/events" className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">
              Plan an event
            </Link>
            <Link to="/gifts" className="rounded-full border border-border bg-card px-6 py-3 text-sm font-medium hover:bg-secondary">
              Send as a gift
            </Link>
          </div>
        </div>
      </section>

      {/* EMAIL SIGNUP */}
      <EmailSignup />

      {/* FAQ PREVIEW */}
      <section className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-8">
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }}
        />
        <h2 className="font-display text-3xl font-semibold">Frequently asked</h2>
        <dl className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card">
          {FAQ.map((f) => (
            <div key={f.q} className="p-5">
              <dt className="font-medium">{f.q}</dt>
              <dd className="mt-2 text-sm text-muted-foreground">{f.a}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-6 text-center">
          <Link to="/faq" className="text-sm font-semibold text-primary hover:underline">All FAQs →</Link>
        </div>
      </section>
    </>
  );
}

function TrustItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 text-muted-foreground">
      {icon}<span>{label}</span>
    </div>
  );
}

function EmailSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "err">("idle");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    const { submitLead } = await import("@/lib/leads.functions");
    const { getStoredUtm } = await import("@/lib/tracking");
    const res = await submitLead({
      data: { type: "newsletter", email, source: "home_inline", utm: getStoredUtm(), payload: {} },
    });
    if ((res as any).ok) {
      track("email_signup", { source: "home_inline" });
      setStatus("ok");
      setEmail("");
    } else setStatus("err");
  }

  return (
    <section className="border-y border-border/60 bg-card/30">
      <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl font-semibold sm:text-4xl">Get 10% off your first order</h2>
        <p className="mt-3 text-muted-foreground">Drop your email. We'll send you a one-time discount and the occasional night-golf tip.</p>
        <form onSubmit={onSubmit} className="mx-auto mt-6 flex max-w-md flex-col gap-2 sm:flex-row">
          <label htmlFor="newsletter-email" className="sr-only">Email address</label>
          <input
            id="newsletter-email"
            type="email"
            required
            aria-label="Email address"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 flex-1 rounded-full border border-input bg-background px-5 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="h-12 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90 disabled:opacity-60"
          >
            {status === "loading" ? "Sending..." : "Send me 10% off"}
          </button>
        </form>
        {status === "ok" && <p className="mt-3 text-sm text-primary">You're in. Check your inbox.</p>}
        {status === "err" && <p className="mt-3 text-sm text-destructive">Something went wrong — try again.</p>}
        <p className="mt-3 text-xs text-muted-foreground">No spam. Unsubscribe any time.</p>
      </div>
    </section>
  );
}

const USE_CASES = [
  { experimentKey: "home_night_golf", label: "Night golf rounds", blurb: "Bright, even glow that lasts a hole.", image: heroImage },
  { experimentKey: "home_backyard", label: "Backyard practice", blurb: "Chip and putt after dinner — no batteries.", image: backyardPractice },
  { experimentKey: "home_mini_golf", label: "Mini golf", blurb: "Glow-in-the-dark putt-putt — backyards, basements, anywhere after dark.", image: miniGolf },
  { experimentKey: "home_gifts", label: "Golf gifts", blurb: "Premium matte-black packaging, ready to give.", image: packagingOpen },
  { experimentKey: "home_camping", label: "Camping & resorts", blurb: "Pack a six and a wedge. Perfect lawn entertainment.", image: campingResort },
  { experimentKey: "home_corporate", label: "Corporate events", blurb: "Memorable outings without the usual corporate-event vibes.", image: corporateEvent },
];

const BENEFITS = [
  "Six unique colors per pack",
  "Charges under any light source",
  "Standard size & weight",
  "Bright after impact, easy to track",
  "Multiple pack sizes",
  "Premium matte-black gift packaging",
];

const STEPS = [
  { title: "Easy Activation", body: "To activate the ball, simply drop it onto a hard surface. The built-in impact sensor turns the glow on automatically, so it's ready to play right away." },
  { title: "Tee up", body: "Place your glowing ball on a tee or the grass. They play like a standard 2-piece ball." },
  { title: "Bounce It Back to Life", body: "The ball automatically turns off after 10 minutes of no impact. A quick bounce on a hard surface turns the glow right back on." },
];

const TESTIMONIALS = [
  { name: "Marcus T.", loc: "Austin, TX", body: "We bought four packs for a bachelor party. Easy to track, super bright, looked amazing on Instagram." },
  { name: "Jenna R.", loc: "Denver, CO", body: "My husband plays a lot of golf and is impossible to shop for. He took these to his weekly group and now everyone wants a pack." },
];

const FAQ = [
  { q: "How long does the glow last?", a: "Brightest in the first few minutes after a full charge, then a gradual fade. Recharge between holes for consistent visibility." },
  { q: "Are these legal for tournament play?", a: "No. Glow Long is a recreational product — not officially tournament approved. Built for night rounds, gifts, parties, and backyard fun." },
  { q: "How do I charge them?", a: "Sunlight, a phone flashlight, or any bright lamp for 30–60 seconds. The brighter the source, the brighter the glow." },
];
